import { journeyToken, JourneyStages } from '@cognitoforms/api/services/journey-context';
import type { ActiveStage, JourneyStage, StageStart } from '@cognitoforms/api/services/journey-context';
import { allPlans } from '@cognitoforms/utils/variant-plans';
import { readPricingExperimentBucketing } from '@cognitoforms/utils/pricing-experiment';

/**
 * What each value of the signup page's `source` parameter means as an entry point. It is a query parameter,
 * so the map is what keeps a reporting dimension from becoming unbounded free text. Most surfaces name the
 * entry point directly; a template signup names the kind of template it came from instead.
 */
const signupOrigins: Record<string, string> = {
	HomePageCTA: 'HomePageCTA',
	PricingPageCTA: 'PricingPageCTA',
	TemplateGalleryCTA: 'TemplateGalleryCTA',
	NavBar: 'NavBar',
	JotformHandoff: 'JotformHandoff',
	templates: 'TemplateGalleryCTA',
	'shared-template': 'SharedTemplateCTA'
};

/**
 * What arriving at the signup page means, from the parameters the arrival carries. `Variant` is what the
 * person was trying to do and stays a small closed set; where they came from goes to `entryPoint`. Pass
 * `isInvitation` for an arrival that came from an invitation link, which reaches the login page rather than
 * the signup page.
 */
export function resolveSignupUserStart(source?: unknown, returnUrl?: unknown, isInvitation = false): StageStart {
	if (isInvitation)
		return { stage: JourneyStages.SignupUser, variant: 'Invite' };

	if (typeof returnUrl === 'string' && returnUrl.toLowerCase().includes('api-connection'))
		return { stage: JourneyStages.SignupUser, variant: 'IntegrationConnect' };

	const origin = typeof source === 'string' && Object.prototype.hasOwnProperty.call(signupOrigins, source) ? signupOrigins[source] : undefined;

	if (origin === 'JotformHandoff')
		return { stage: JourneyStages.SignupUser, variant: 'Jotform', entryPoint: origin };

	const start: StageStart = { stage: JourneyStages.SignupUser, variant: 'Website' };
	if (origin)
		start.entryPoint = origin;
	return start;
}

const templateSignupSources = ['templates', 'shared-template'];

const MAX_TEMPLATE_ID_LENGTH = 48;

export function resolveTemplateSignupStart(source?: unknown): StageStart | null {
	if (typeof source !== 'string' || !templateSignupSources.includes(source))
		return null;

	return { stage: JourneyStages.CreateForm, variant: 'Template', entryPoint: signupOrigins[source] };
}

export function resolveTemplateSignupId(details?: unknown): string | undefined {
	if (typeof details !== 'string')
		return undefined;

	return journeyToken(details.slice(details.lastIndexOf('-') + 1), MAX_TEMPLATE_ID_LENGTH) || undefined;
}

/**
 * Billing intervals formatted to be reported by the customer journey reporting
 */
const reportedIntervals: Record<string, string> = {
	monthly: 'Monthly',
	annual: 'Annual',
	legacy: 'Legacy'
};

const MAX_CAMPAIGN_KEY_LENGTH = 32;

const DIMENSION_NO_VALUE = 'None';

/** The offer a signup carries, which rides on the `returnUrl` the plan it chose sent it to signup with. */
function signupOffer(returnUrl?: unknown): URLSearchParams {
	if (typeof returnUrl !== 'string')
		return new URLSearchParams();

	const query = returnUrl.indexOf('?');
	if (query < 0)
		return new URLSearchParams();

	return new URLSearchParams(returnUrl.slice(query + 1));
}

function targetPlan(offer: URLSearchParams): string {
	const plan = offer.get('plan')?.toLowerCase();
	return allPlans.find(name => name.toLowerCase() === plan) ?? DIMENSION_NO_VALUE;
}

/**
 * The billing term a dimension reports, or `None` when there is no term to report - a free plan has no
 * cadence, so reporting the UI's default for one would invent a term the organization never had.
 */
export function reportedInterval(interval: string | null | undefined): string {
	const key = interval?.toLowerCase();
	if (key && Object.prototype.hasOwnProperty.call(reportedIntervals, key))
		return reportedIntervals[key];

	return DIMENSION_NO_VALUE;
}

/**
 * The pricing tier of the plan an organization is on, or `null` when it is on the free plan. An organization
 * with no subscription is handed the quoted tier's free plan, so its tier would only echo the quoted one.
 */
export function reportedCurrentPricingTier(plan: { readonly IsPaidPlan?: boolean; readonly PricingTierCode?: string | null } | null | undefined): string | null {
	return plan?.IsPaidPlan ? plan.PricingTierCode || null : null;
}

/**
 * The plan, cadence, pricing-tier and pricing-experiment dimensions common to every `ConvertToPaid`/`ChangePlan`
 * Start and the CTA click that opens either stage. `currentInterval` is reported as-is through
 * {@link reportedInterval}, so pass `null` where the org has no cadence to report (never paid, or on a
 * trial) rather than inventing one. `targetPricingTier` is the code of the tier the page was quoted and shows,
 * which any purchase lands on; `currentPricingTier` is the tier of the plan the org is on, from
 * {@link reportedCurrentPricingTier}, so the two differ when a change moves the org onto new pricing.
 */
export function resolveCurrentPlanDimensions(currentPlanName: string | null, currentInterval: string | null, arm: string | null | undefined, experiment: string | null | undefined, isTrial?: boolean, targetPricingTier?: string | null, currentPricingTier?: string | null): Record<string, string> {
	const dimensions: Record<string, string> = {
		CurrentPlan: currentPlanName || DIMENSION_NO_VALUE,
		CurrentInterval: reportedInterval(currentInterval),
		CurrentPricingTier: currentPricingTier || DIMENSION_NO_VALUE,
		TargetPricingTier: targetPricingTier || DIMENSION_NO_VALUE,
		Arm: arm ?? DIMENSION_NO_VALUE,
		Experiment: experiment ?? DIMENSION_NO_VALUE
	};

	if (isTrial !== undefined)
		dimensions.IsTrial = isTrial ? 'True' : 'False';

	return dimensions;
}

type ContactSalesOrganization = {
	readonly Plan?: { readonly IsPaidPlan?: boolean; readonly Product?: { readonly Name?: string } | null } | null;
	readonly BillingMode?: string | null;
	readonly IsTrial?: boolean;
	readonly Role?: string | null;
};

export function resolveContactSalesDimensions(organization: ContactSalesOrganization | null | undefined): Record<string, string> {
	const isTrial = organization?.IsTrial === true;

	let currentInterval: string | null = null;
	if (organization?.Plan?.IsPaidPlan && !isTrial)
		currentInterval = organization.BillingMode ?? null;

	return {
		CurrentPlan: organization?.Plan?.Product?.Name || DIMENSION_NO_VALUE,
		CurrentInterval: reportedInterval(currentInterval),
		IsTrial: String(isTrial),
		UserRole: organization?.Role || DIMENSION_NO_VALUE
	};
}

/**
 * What the organization being created is for: the plan and term the visitor chose before there was an
 * organization to charge, and the arm they were bucketed into when they chose it. Reported there
 * because it is the only place they are known — a trial that never converts completes no purchase for
 * them to be read back from. Every dimension is always reported, so a signup carrying no offer is a value
 * rather than a gap.
 */
export function resolveCreateOrganizationDimensions(returnUrl?: unknown): Record<string, string> {
	const offer = signupOffer(returnUrl);
	const bucketing = readPricingExperimentBucketing();
	const campaign = journeyToken(bucketing?.campaign, MAX_CAMPAIGN_KEY_LENGTH);

	const dimensions: Record<string, string> = {
		TargetPlan: targetPlan(offer),
		TargetInterval: reportedInterval(offer.get('interval')),
		Arm: DIMENSION_NO_VALUE,
		Experiment: DIMENSION_NO_VALUE
	};

	if (bucketing && campaign) {
		dimensions.Arm = bucketing.arm;
		dimensions.Experiment = campaign;
	}

	return dimensions;
}

/**
 * What arriving at the organization-creation page means. Only the signup redirect names itself, so anything
 * else is someone who already had an account and has no organization to go to — one whose only organization
 * was deleted, or who was removed from it.
 */
export function resolveCreateOrganizationStart(entry?: unknown): StageStart {
	return { stage: JourneyStages.CreateOrganization, variant: entry === 'signup' ? 'Signup' : 'OrphanedUser' };
}

export const authProviders: Record<string, string> = {
	Email: 'Email',
	Google: 'Google',
	Facebook: 'Facebook',
	Microsoft: 'Microsoft',
	Custom: 'OpenIdConnect',
	OpenIdConnect: 'OpenIdConnect'
};

export function resolveAuthProvider(provider?: unknown): string | undefined {
	return typeof provider === 'string' && Object.prototype.hasOwnProperty.call(authProviders, provider) ? authProviders[provider] : undefined;
}

/**
 * What each value of the plan page's `source` parameter means as an arrival intent. The keys are the values
 * actually in use; anything else is treated as no source at all.
 */
export const upgradeIntents: Record<string, string> = {
	unavailfeat: 'LimitReached',
	nearstoragelimit: 'LimitReached',
	storagelimits: 'LimitReached',
	nearentrylimits: 'LimitReached',
	entrylimits: 'LimitReached',
	feature: 'FeatureLocked',
	'tryitnow-feature': 'FeatureLocked',
	'upgrade-permissions': 'FeatureLocked',
	'upgrade-folders': 'FeatureLocked',
	workflowtasks: 'FeatureLocked',
	orgsettings: 'BillingSettings',
	'upgrade-now': 'UpgradeCTA',
	freetrial: 'Signup',
	trialend: 'TrialEnded',
	'discount-notification': 'DiscountOffer',
	'billing-alert': 'BillingAlert',
	'billing-email': 'BillingEmail'
};

const MAX_UPGRADE_DETAIL_LENGTH = 48;

/**
 * Reduces the `details` parameter — the feature code that was gated — to something safe to group by. There
 * are too many feature codes to allowlist, so it is bounded by shape instead.
 */
function upgradeDetail(details: unknown): string | undefined {
	if (typeof details !== 'string')
		return undefined;
	return journeyToken(details, MAX_UPGRADE_DETAIL_LENGTH) || undefined;
}

/**
 * What reaching the plan page means, from the parameters the arrival carries. `Variant` says what kind of
 * pressure brought them and stays a small closed set; the raw `source` goes to `entryPoint` and the feature
 * code to `detail`.
 */
function resolveArrival(stage: JourneyStage, source?: unknown, details?: unknown, isSignUp = false): StageStart {
	const start: StageStart = {
		stage,
		variant: isSignUp ? 'Signup' : 'Direct'
	};

	const detail = upgradeDetail(details);
	if (detail)
		start.detail = detail;

	if (typeof source !== 'string' || !Object.prototype.hasOwnProperty.call(upgradeIntents, source))
		return start;

	if (!isSignUp)
		start.variant = upgradeIntents[source];
	start.entryPoint = source;
	return start;
}

export function resolveUpgradeStart(source?: unknown, details?: unknown, isSignUp = false): StageStart {
	return resolveArrival(JourneyStages.ConvertToPaid, source, details, isSignUp);
}

export function resolveChangePlanStart(source?: unknown, details?: unknown): StageStart {
	return resolveArrival(JourneyStages.ChangePlan, source, details);
}

export type FieldKind = 'Text' | 'Choice' | 'Amount' | 'Card';

const checkoutFields: Record<string, FieldKind> = {
	FirstName: 'Text',
	LastName: 'Text',
	Email: 'Text',
	Company: 'Text',
	NameOnCard: 'Text',
	AddressLine1: 'Text',
	AddressLine2: 'Text',
	City: 'Text',
	State: 'Text',
	PostalCode: 'Text',
	PurchaseOrderNumber: 'Text',
	Country: 'Choice',
	OrganizationType: 'Choice',
	PaymentMethod: 'Choice',
	TaxId: 'Choice',
	VatOptOut: 'Choice',
	CardNumber: 'Card',
	CardExpiry: 'Card',
	CardCvc: 'Card',
	Users: 'Amount',
	Guests: 'Amount',
	Documents: 'Amount',
	Storage: 'Amount',
	ApiUsage: 'Amount',
	AdditionalFunds: 'Amount'
};

const billingProperties: Record<string, string> = {
	FirstName: 'FirstName',
	LastName: 'LastName',
	EmailAddress: 'Email',
	Company: 'Company',
	Line1: 'AddressLine1',
	Line2: 'AddressLine2',
	City: 'City',
	State: 'State',
	PostalCode: 'PostalCode',
	CountryCode: 'Country',
	OrganizationType: 'OrganizationType',
	TaxIds: 'TaxId',
	PurchaseOrderNumber: 'PurchaseOrderNumber',
	IsVATRegistrationOptOut: 'VatOptOut'
};

const quantityFields: Record<string, string> = {
	users: 'Users',
	guests: 'Guests',
	documentGeneration: 'Documents',
	storage: 'Storage',
	apiUsage: 'ApiUsage',
	additionalFunds: 'AdditionalFunds'
};

const organizationTypes: Record<string, string> = {
	Business: 'Business',
	Individual: 'Individual'
};

const paymentMethods: Record<string, string> = {
	creditcard: 'CreditCard',
	invoice: 'Invoice'
};

const OTHER_CHOICE = 'Other';

export function resolveFieldKind(field: unknown): FieldKind | undefined {
	return typeof field === 'string' && Object.prototype.hasOwnProperty.call(checkoutFields, field) ? checkoutFields[field] : undefined;
}

export function resolveBillingField(property: unknown): string | undefined {
	return typeof property === 'string' && Object.prototype.hasOwnProperty.call(billingProperties, property) ? billingProperties[property] : undefined;
}

export function resolveQuantityField(model: unknown): string | undefined {
	return typeof model === 'string' && Object.prototype.hasOwnProperty.call(quantityFields, model) ? quantityFields[model] : undefined;
}

function lookup(map: Record<string, string>, value: unknown): string | undefined {
	return typeof value === 'string' && Object.prototype.hasOwnProperty.call(map, value) ? map[value] : undefined;
}

function enteredTaxIdTypes(value: unknown): string[] {
	if (!Array.isArray(value))
		return [];

	const types = value
		.filter(entry => entry && typeof entry === 'object' && typeof entry.Type === 'string' && typeof entry.Id === 'string' && entry.Id.trim() !== '')
		.map(entry => (entry.Type as string).toLowerCase())
		.filter(type => /^[a-z0-9_]{2,32}$/.test(type));

	return Array.from(new Set(types)).sort();
}

export function hasFieldValue(field: string, value: unknown): boolean {
	if (field === 'TaxId')
		return enteredTaxIdTypes(value).length > 0;
	if (typeof value === 'number')
		return resolveFieldKind(field) === 'Amount' ? value > 0 : Number.isFinite(value);
	if (typeof value === 'boolean')
		return value;
	if (typeof value === 'string')
		return value.trim() !== '';
	if (Array.isArray(value))
		return value.length > 0;

	return value !== null && value !== undefined;
}

export function resolveFieldChoice(field: string, value: unknown): string | undefined {
	switch (field) {
		case 'Country': {
			const code = typeof value === 'string' ? value.trim().toUpperCase() : '';
			return /^[A-Z]{2}$/.test(code) ? code : OTHER_CHOICE;
		}
		case 'OrganizationType':
			return lookup(organizationTypes, value) ?? OTHER_CHOICE;
		case 'PaymentMethod':
			return lookup(paymentMethods, value) ?? OTHER_CHOICE;
		case 'VatOptOut':
			return value === true ? 'True' : 'False';
		case 'TaxId': {
			const types = enteredTaxIdTypes(value);
			return types.length ? types.join('-') : DIMENSION_NO_VALUE;
		}
		default:
			return undefined;
	}
}

export type CheckoutContext = {
	isFirstPaidPlan: boolean;
	targetPlan: string;
	targetInterval: string;
	currentPlan: string | null;
	currentInterval: string | null;
	isTrial: boolean;
	isWalletRefill?: boolean;
	targetPricingTier?: string | null;
	currentPricingTier?: string | null;
	arm?: string | null;
	experiment?: string | null;
	source?: string;
	details?: string;
};

function checkoutVariant(context: CheckoutContext): string {
	if (context.isWalletRefill)
		return 'WalletRefill';

	return context.isFirstPaidPlan ? 'NewSubscription' : 'PlanChange';
}

export function resolveCheckoutStart(context: CheckoutContext, arrival: ActiveStage | null): StageStart {
	const fallback = resolveUpgradeStart(context.source, context.details);
	const start: StageStart = {
		stage: JourneyStages.Checkout,
		variant: checkoutVariant(context)
	};

	const entryPoint = arrival?.entryPoint ?? fallback.entryPoint;
	if (entryPoint)
		start.entryPoint = entryPoint;

	const detail = arrival?.detail ?? fallback.detail;
	if (detail)
		start.detail = detail;

	return start;
}

export function resolveCheckoutDimensions(context: CheckoutContext, arrival: ActiveStage | null): Record<string, string> {
	const dimensions: Record<string, string> = {
		...resolveCurrentPlanDimensions(context.currentPlan, context.currentInterval, context.arm, context.experiment, undefined, context.targetPricingTier, context.currentPricingTier),
		TargetPlan: context.targetPlan || DIMENSION_NO_VALUE,
		TargetInterval: reportedInterval(context.targetInterval),
		IsTrial: context.isTrial ? 'True' : 'False'
	};

	if (arrival)
		dimensions.ParentStageCorrelationId = arrival.stageCorrelationId;

	return dimensions;
}

const checkoutErrorSources = ['Validation', 'Server', 'Card', 'Authentication', 'TaxId', 'Payment', 'Submit', 'Quote', 'Pricing'];

const fieldErrorSources = ['Validation', 'Server'];

const MAX_ERROR_CODE_LENGTH = 64;

export interface CheckoutErrorFact {
	source: string;
	code?: string;
	declineCode?: string;
	field?: string;
	fieldDimensions?: Record<string, string>;
	statusCode?: number;
}

export interface CheckoutErrorSignal {
	dimensions: Record<string, string>;
	measurements: Record<string, number>;
}

export function resolveCardErrorField(code: unknown): string | undefined {
	if (typeof code !== 'string')
		return undefined;

	if (code.includes('number'))
		return 'CardNumber';
	if (code.includes('expiry'))
		return 'CardExpiry';
	if (code.includes('cvc'))
		return 'CardCvc';

	return undefined;
}

export function resolveCheckoutError(fact: CheckoutErrorFact): CheckoutErrorSignal | undefined {
	if (!fact || !checkoutErrorSources.includes(fact.source))
		return undefined;

	const field = resolveFieldKind(fact.field) ? fact.field : undefined;
	if (!field && fieldErrorSources.includes(fact.source))
		return undefined;

	const dimensions: Record<string, string> = { ...(field ? fact.fieldDimensions : undefined), ErrorSource: fact.source };
	const measurements: Record<string, number> = {};

	if (field)
		dimensions.Field = field;

	const code = journeyToken(typeof fact.code === 'string' ? fact.code : '', MAX_ERROR_CODE_LENGTH);
	if (code)
		dimensions.ErrorCode = code;

	const declineCode = journeyToken(typeof fact.declineCode === 'string' ? fact.declineCode : '', MAX_ERROR_CODE_LENGTH);
	if (declineCode)
		dimensions.DeclineCode = declineCode;

	if (typeof fact.statusCode === 'number' && Number.isFinite(fact.statusCode) && fact.statusCode > 0)
		measurements.StatusCode = fact.statusCode;

	return { dimensions, measurements };
}
